import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star } from 'lucide-react';

const QUOTES = [
    {
        text:
            'My home got a facelift, much needed! Don\u2019t negotiate with quality — perfection at its finest! Thank you to these gents for all the hard work!',
        by: 'Emilio Del Sol',
        role: 'Homeowner',
    },
    {
        text:
            'I had these guys paint both the inside of my house and the outside all in one go — about a week and a half including preparation. Speed and accuracy does not miss. These men are artists!!!',
        by: 'Ivan Dominguez',
        role: 'Homeowner',
    },
    {
        text:
            'Insanely fast with an exquisite attention to detail. I\u2019ve dealt with many painters — I have rental properties throughout the state, Santa Fe, ABQ, Rio, etc. — and I\u2019ve never experienced such quality and speed. Skeptical at first when just three or four guys showed up (the dad, two sons, and I think an employee), but they blew through 4 bedrooms and 3 bathrooms in 2.5 days. Will continue to use them.',
        by: 'Tyler Crankfield',
        role: 'Property Investor · Multi-city',
    },
    {
        text:
            'They got everything done super quick and it looks fantastic. The whole team was super friendly as well.',
        by: 'Frankki Rance',
        role: 'Homeowner',
    },
    {
        text:
            'Our two-story house needed all the trim painted — windows, soffits, doors, garage door, gables — the weather can be quite harsh here. Got a very reasonable quote from MSD, agreed to it, and within a week the work was done. Looks very nice. Moises and his crew are experienced, capable professionals who get the job done. Pleasant and easy to work with, and they kept the job site clean.',
        by: 'Rick Koehler',
        role: 'Homeowner · Two-story trim project',
    },
];

const Card = ({ q, i, span }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.25 });
    return (
        <motion.figure
            ref={ref}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: i * 0.08 }}
            className={`col-span-12 ${span}`}
            data-testid={`testimonial-${i}`}
        >
            <div className="relative border-t border-white/10 pt-10 pr-4 h-full flex flex-col">
                <div className="flex items-center gap-1.5 mb-6 text-adobe-500" aria-label="Five out of five stars">
                    {[0, 1, 2, 3, 4].map((s) => (
                        <Star key={s} size={13} strokeWidth={1.5} fill="currentColor" />
                    ))}
                    <span className="ml-2 font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400">5.0 · Google Review</span>
                </div>
                <blockquote className="font-display italic font-light text-xl md:text-2xl lg:text-[1.75rem] leading-[1.35] text-bone-100 tracking-tight flex-1">
                    <span className="font-display text-3xl text-adobe-500 mr-1 align-top leading-none">“</span>
                    {q.text}
                </blockquote>
                <figcaption className="mt-8 flex items-center gap-4">
                    <span className="w-8 h-px bg-bone-400" />
                    <div>
                        <div className="font-body text-sm text-bone-100">{q.by}</div>
                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400 mt-1">
                            {q.role}
                        </div>
                    </div>
                </figcaption>
            </div>
        </motion.figure>
    );
};

// Editorial asymmetric layout: 3 cols on top, 2 wider cards below
const SPANS = ['md:col-span-4', 'md:col-span-4', 'md:col-span-4', 'md:col-span-7', 'md:col-span-5'];

export const Testimonials = () => {
    return (
        <section
            data-testid="msd-testimonials"
            className="relative z-20 bg-ink-900 px-6 md:px-10 pb-24 md:pb-40"
        >
            <div className="mx-auto max-w-[1600px]">
                <div className="pt-6 pb-12 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-10 h-px bg-adobe-500" />
                            <span className="eyebrow">Field notes · 5-star reviews</span>
                        </div>
                        <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1] text-bone-100">
                            What clients say.
                        </h2>
                    </div>
                    <div className="hidden md:flex md:col-span-6 items-end justify-end">
                        <div className="max-w-sm text-right">
                            <div className="flex items-center justify-end gap-1.5 mb-2 text-adobe-500">
                                {[0,1,2,3,4].map((s) => (
                                    <Star key={s} size={16} strokeWidth={1.5} fill="currentColor" />
                                ))}
                            </div>
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-bone-400">
                                Verified homeowners · Rio Rancho · Albuquerque · Santa Fe
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-8 md:gap-16">
                    {QUOTES.map((q, i) => <Card q={q} i={i} span={SPANS[i]} key={i} />)}
                </div>

                <div className="mt-16 flex justify-center">
                    <a
                        href="https://www.google.com/search?sca_esv=dd11972937bd8ee9&q=MSD+Painting+Services+Reviews"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="testimonials-google-cta"
                        className="btn-pill btn-pill--ghost"
                    >
                        <Star size={13} strokeWidth={1.5} fill="currentColor" className="text-brand-red" />
                        Read all reviews on Google
                    </a>
                </div>
            </div>
        </section>
    );
};
